import { parseBackendDxfJson, parseDxfJson, type ParsedFloorPlan, type DxfJsonDocument } from './dxf';
import { extractEquipmentFromDxfJson } from './dxfEquipmentExtractor';
import type { EquipmentInstance } from '../features/equipment/equipmentTypes';

export type ParsedLosslessFloorPlan = ParsedFloorPlan & {
  name: string;
  /** Slim stub — full entity tree is discarded after parse to keep the UI thread light. */
  document: DxfJsonDocument;
  extractedEquipment: EquipmentInstance[];
  source: 'backend-json' | 'dxf-json';
};

function unwrapDocument(payload: unknown): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object') return null;
  const envelope = payload as Record<string, unknown>;
  if (Array.isArray(envelope.entities) || Array.isArray(envelope.blocks)) {
    return envelope;
  }
  const nested = envelope.data ?? envelope.result ?? envelope.payload;
  if (nested && typeof nested === 'object') {
    const nestedObj = nested as Record<string, unknown>;
    if (Array.isArray(nestedObj.entities) || Array.isArray(nestedObj.blocks)) {
      return nestedObj;
    }
  }
  return null;
}

function resolveName(doc: Record<string, unknown>, fallbackName: string): string {
  const fileName = doc.file_name;
  const name = doc.name;
  if (typeof fileName === 'string' && fileName.trim()) return fileName.trim();
  if (typeof name === 'string' && name.trim()) return name.trim();
  return fallbackName;
}

/**
 * Convert a lossless-json API payload into drawable geometry.
 * Prefers the backend entity shape (single pass) and only falls back to the
 * external DXF-JSON parser when that yields no polylines.
 */
export function parseLosslessFloorPlanPayload(
  payload: unknown,
  fallbackName: string
): ParsedLosslessFloorPlan {
  const doc = unwrapDocument(payload);
  if (!doc) {
    throw new Error('Backend lossless JSON response is not a valid DXF document (missing "entities").');
  }

  let parsed: ParsedFloorPlan | null;
  let source: 'backend-json' | 'dxf-json' = 'backend-json';

  try {
    parsed = parseBackendDxfJson(doc);
    source = 'backend-json';
  } catch {
    parsed = null;
  }

  if (!parsed || parsed.polylines.length === 0) {
    try {
      const viaDxf = parseDxfJson(doc as unknown as DxfJsonDocument);
      if (!parsed || viaDxf.polylines.length > parsed.polylines.length) {
        parsed = viaDxf;
        source = 'dxf-json';
      }
    } catch {
      // Keep backend result if present.
    }
  }

  if (!parsed) {
    throw new Error('Backend lossless JSON response could not be parsed.');
  }

  let extractedEquipment: EquipmentInstance[];
  try {
    extractedEquipment = extractEquipmentFromDxfJson(doc, parsed.transform);
  } catch {
    extractedEquipment = [];
  }

  return {
    ...parsed,
    name: resolveName(doc, fallbackName),
    // Do not retain the 50MB+ entity tree on the main thread / in Redux callers.
    document: { entities: [], name: resolveName(doc, fallbackName) },
    extractedEquipment,
    source,
  };
}

export function parseLosslessFloorPlanText(
  jsonText: string,
  fallbackName: string
): ParsedLosslessFloorPlan {
  let payload: unknown;
  try {
    payload = JSON.parse(jsonText);
  } catch (err) {
    throw new Error(
      `Backend lossless JSON is not valid JSON: ${err instanceof Error ? err.message : String(err)}`
    );
  }
  return parseLosslessFloorPlanPayload(payload, fallbackName);
}
